import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import * as Sentry from '@sentry/react'

/** Request body for POST /fhir/patient/guardian-contact. */
export interface GuardianContactFhirRequest {
  accessToken: string
  organizationId: string
  /** FHIR Patient logical id of the proband. */
  fhirPatientId: string
}

/** Raw FHIR Address on a guardian contact; every sub-field is optional. */
export interface GuardianContactAddress {
  use?: string | null
  line?: string[] | null
  city?: string | null
  state?: string | null
  postalCode?: string | null
  country?: string | null
  text?: string | null
}

/** One telecom entry on a guardian contact (FHIR shape). */
export interface GuardianContactTelecomItem {
  system?: string | null
  value?: string | null
  use?: string | null
}

/** One guardian contact from POST /fhir/patient/guardian-contact. */
export interface GuardianContactItem {
  /** Guardian display name (contact.name.text). */
  name?: string | null
  address?: GuardianContactAddress | null
  telecom: GuardianContactTelecomItem[]
  /** Kinship text like "Mother"; null when only Legal Guardian / Emergency Contact. */
  relationship?: string | null
  /** Always true for returned items (selection is gated on it). */
  isLegalGuardian?: boolean | null
  /** True when the contact is also coded as an emergency contact. */
  isEmergencyContact?: boolean | null
}

function normalizeGuardianContactItem(o: Record<string, unknown>): GuardianContactItem {
  const name = o.name
  const relationship = o.relationship
  const telecomRaw = o.telecom
  const telecom = Array.isArray(telecomRaw) ? (telecomRaw as GuardianContactTelecomItem[]) : []
  const addressRaw = o.address
  const address = addressRaw != null && typeof addressRaw === 'object' ? (addressRaw as GuardianContactAddress) : null
  return {
    name: typeof name === 'string' ? name : name === null ? null : undefined,
    address,
    telecom,
    relationship: typeof relationship === 'string' ? relationship : relationship === null ? null : undefined,
    isLegalGuardian: o.isLegalGuardian === true,
    isEmergencyContact: o.isEmergencyContact === true,
  }
}

/**
 * Fetch the proband's legal guardian contact(s) from Epic FHIR via provider-api.
 * Requires Epic launch context (`epicState`) for accessToken, organizationId,
 * and the proband's FHIR Patient logical id.
 *
 * @returns Guardian contacts on success (possibly empty — a patient with no
 *   legal guardian is normal, not an error), or `null` when the Epic session /
 *   required inputs are missing or the request failed.
 */
export async function fetchGuardianContacts(
  epicState: string,
  signal?: AbortSignal
): Promise<GuardianContactItem[] | null> {
  const epic = EpicAuthClient.getInstance()
  if (!epic.hasSession(epicState)) {
    return null
  }
  const accessToken = epic.getAccessToken(epicState)
  const organizationId = epic.getOrgId(epicState)
  const fhirPatientId = epic.getContext(epicState).fhirPatientId
  if (!accessToken || !organizationId || !fhirPatientId?.trim()) {
    return null
  }

  const body: GuardianContactFhirRequest = {
    accessToken,
    organizationId,
    fhirPatientId: fhirPatientId.trim(),
  }

  const providerApi = ProviderAPIClient.getInstance()
  let response: Response
  try {
    response = await providerApi.post(
      '/fhir/patient/guardian-contact',
      body,
      { 'Content-Type': 'application/json' },
      true,
      signal
    )
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err
    }
    Sentry.captureException(err instanceof Error ? err : new Error('Guardian contact lookup failed'), {
      tags: { operation: 'fhir.patient.guardian_contact' },
    })
    return null
  }

  if (!response.ok) {
    Sentry.captureException(new Error(`Guardian contact lookup failed: ${response.status}`), {
      tags: { operation: 'fhir.patient.guardian_contact', status: String(response.status) },
    })
    return null
  }

  let json: unknown
  try {
    json = await response.json()
  } catch {
    return null
  }

  if (!Array.isArray(json)) {
    return null
  }

  return json
    .filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object')
    .map((row) => normalizeGuardianContactItem(row))
}
